import { type FC } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../../common/integrations'
import { useGetMeQuery, useGetUserIntegrationInfoQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { CalendarProvider, MeetingPlatformProvider, type UserSchedulingSettings } from '../../../../models/Scheduling'
import { SettingsCard, SettingsRow, SettingsSection } from '../../../Setup/Scheduling/sections/SettingsSection'
import { type UpdateUserSettings } from '../SchedulingProfileTab'
import {
  getIntegrationConnection,
  getSchedulingCapability,
  type IntegrationConnection,
  IntegrationConnectionStatus,
  resolveIntegrationsOnCalendarSelect,
} from './schedulingIntegrationStatus'

interface SectionProps {
  userSettings: UserSchedulingSettings
  isSaving?: boolean
  onUpdate: UpdateUserSettings
}

interface CalendarOption {
  serviceId: IntegrationServiceId
  provider: CalendarProvider
  label: string
}

const GOOGLE_CALENDAR_OPTION: CalendarOption = { serviceId: IntegrationServiceId.GOOGLE_CALENDAR, provider: CalendarProvider.GOOGLE, label: 'Google Calendar' }
const OUTLOOK_CALENDAR_OPTION: CalendarOption = {
  serviceId: IntegrationServiceId.OUTLOOK_CALENDAR,
  provider: CalendarProvider.OUTLOOK,
  label: 'Outlook Calendar',
}

const CALENDAR_OPTIONS: Array<CalendarOption> = [GOOGLE_CALENDAR_OPTION, OUTLOOK_CALENDAR_OPTION]

interface PlatformOption {
  serviceId: IntegrationServiceId
  provider: MeetingPlatformProvider
  label: string
  nativeCalendar?: CalendarOption
}

const PLATFORM_OPTIONS: Array<PlatformOption> = [
  { serviceId: IntegrationServiceId.ZOOM, provider: MeetingPlatformProvider.ZOOM, label: 'Zoom' },
  { serviceId: IntegrationServiceId.MEET, provider: MeetingPlatformProvider.GOOGLE_MEET, label: 'Google Meet', nativeCalendar: GOOGLE_CALENDAR_OPTION },
  { serviceId: IntegrationServiceId.TEAMS, provider: MeetingPlatformProvider.MS_TEAMS, label: 'Microsoft Teams', nativeCalendar: OUTLOOK_CALENDAR_OPTION },
]

const INTEGRATION_SETTINGS_PATH = '/account/integration-settings/integrations'

const BASE_CARD_CLASS = 'd-flex align-items-center gap-2 p-3 rounded-3 flex-1'

type CardAction = 'connect' | 'reconnect'

interface IntegrationOptionCardProps {
  serviceId: IntegrationServiceId
  label: string
  description?: string
  selectable: boolean
  selected: boolean
  action?: CardAction
  onSelect: () => void
}

const IntegrationOptionCard: FC<IntegrationOptionCardProps> = ({ serviceId, label, description, selectable, selected, action, onSelect }) => {
  const navigate = useNavigate()

  const content = (
    <>
      <i
        className={classNames(
          selected ? 'bi bi-record-circle-fill zocks-text-primary' : 'bi bi-circle',
          !selected && (selectable ? 'zocks-text-gray-500' : 'zocks-text-gray-400'),
        )}
      />
      <IntegrationIcon serviceId={serviceId} width='1.5rem' height='1.5rem' />
      <span className='d-flex flex-column flex-grow-1 overflow-hidden text-start'>
        <span className={classNames('paragraph-small bold', selectable ? 'zocks-text-gray-700' : 'zocks-text-gray-500')}>{label}</span>
        {description ? (
          <span className={classNames('paragraph-small text-truncate', selectable ? 'zocks-text-gray-600' : 'zocks-text-gray-500')}>{description}</span>
        ) : null}
        {action ? (
          <button
            type='button'
            className='btn btn-link p-0 paragraph-small bold text-start'
            onClick={() => navigate(`${INTEGRATION_SETTINGS_PATH}/${serviceId}`)}
          >
            {action === 'reconnect' ? 'Reconnect' : 'Connect'}
          </button>
        ) : null}
      </span>
    </>
  )

  if (!selectable) {
    return <div className={classNames(BASE_CARD_CLASS, 'border zocks-border-color-gray-300 zocks-bg-color-gray-100')}>{content}</div>
  }

  return (
    <button
      type='button'
      role='radio'
      aria-checked={selected}
      aria-label={label}
      onClick={onSelect}
      className={classNames(
        BASE_CARD_CLASS,
        'text-start zocks-bg-color-white',
        selected ? 'zocks-border-color-primary border-2 border' : 'border zocks-border-color-gray-300',
      )}
    >
      {content}
    </button>
  )
}

const cardAction = (connection: IntegrationConnection): CardAction | undefined => {
  if (connection.status === IntegrationConnectionStatus.NEEDS_RECONNECT) {
    return 'reconnect'
  }
  return connection.status === IntegrationConnectionStatus.NOT_CONNECTED ? 'connect' : undefined
}

interface PlatformOptionCardProps {
  option: PlatformOption
  zoomConnection: IntegrationConnection
  selectedCalendar?: CalendarOption
  selectedPlatform?: MeetingPlatformProvider
  onSelect: (provider: MeetingPlatformProvider) => void
}

const PlatformOptionCard: FC<PlatformOptionCardProps> = ({ option, zoomConnection, selectedCalendar, selectedPlatform, onSelect }) => {
  if (option.provider === MeetingPlatformProvider.ZOOM) {
    const isZoomSelectable = zoomConnection.status === IntegrationConnectionStatus.READY
    return (
      <IntegrationOptionCard
        serviceId={option.serviceId}
        label={option.label}
        description={zoomConnection.email}
        selectable={isZoomSelectable}
        selected={isZoomSelectable && selectedPlatform === option.provider}
        action={cardAction(zoomConnection)}
        onSelect={() => onSelect(option.provider)}
      />
    )
  }
  const isNative = option.nativeCalendar?.provider === selectedCalendar?.provider
  return (
    <IntegrationOptionCard
      serviceId={option.serviceId}
      label={option.label}
      description={isNative ? `via ${option.nativeCalendar?.label}` : `Available alongside ${option.nativeCalendar?.label}`}
      selectable={isNative}
      selected={isNative && selectedPlatform === option.provider}
      onSelect={() => onSelect(option.provider)}
    />
  )
}

export const SchedulingIntegrationsSection: FC<SectionProps> = ({ userSettings, isSaving = false, onUpdate }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { data: me } = useGetMeQuery({ accountId: authenticatedUser.accountId })
  const { data: integrationInfo } = useGetUserIntegrationInfoQuery({ accountId: authenticatedUser.accountId, userId: authenticatedUser.id })

  const connectionFor = (serviceId: IntegrationServiceId, provider: CalendarProvider | MeetingPlatformProvider): IntegrationConnection =>
    getIntegrationConnection(me?.integrations, integrationInfo?.data.problems, serviceId, getSchedulingCapability(userSettings.integrations, provider))

  const zoomConnection = connectionFor(IntegrationServiceId.ZOOM, MeetingPlatformProvider.ZOOM)
  const isZoomSelectable = zoomConnection.status === IntegrationConnectionStatus.READY

  const selectedCalendarOption = CALENDAR_OPTIONS.find(
    (option) =>
      option.provider === userSettings.integrations?.selectedCalendar &&
      connectionFor(option.serviceId, option.provider).status === IntegrationConnectionStatus.READY,
  )

  const selectCalendar = (provider: CalendarProvider) => {
    if (provider === selectedCalendarOption?.provider) {
      return
    }
    void onUpdate({ integrations: resolveIntegrationsOnCalendarSelect(userSettings.integrations, provider, isZoomSelectable) })
  }

  const selectPlatform = (provider: MeetingPlatformProvider) => {
    if (provider === userSettings.integrations?.selectedMeetingPlatform) {
      return
    }
    void onUpdate({ integrations: { ...userSettings.integrations, selectedMeetingPlatform: provider } })
  }

  return (
    <SettingsSection title='Integrations' subtitle='Connect the calendar and meeting platform for scheduled events.' isSaving={isSaving}>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Scheduling calendar</span>
            <span className='paragraph-small zocks-text-gray-600'>
              Scheduled events are added to this calendar. Connect calendars in <NavLink to={INTEGRATION_SETTINGS_PATH}>Integration settings</NavLink>.
            </span>
          </div>
          <div role='radiogroup' aria-label='Scheduling calendar' className='d-flex flex-wrap gap-2'>
            {CALENDAR_OPTIONS.map((option) => {
              const connection = connectionFor(option.serviceId, option.provider)
              return (
                <IntegrationOptionCard
                  key={option.serviceId}
                  serviceId={option.serviceId}
                  label={option.label}
                  description={connection.email}
                  selectable={connection.status === IntegrationConnectionStatus.READY}
                  selected={option.provider === selectedCalendarOption?.provider}
                  action={cardAction(connection)}
                  onSelect={() => selectCalendar(option.provider)}
                />
              )
            })}
          </div>
        </SettingsRow>
        <SettingsRow className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Meeting platform</span>
            <span className='paragraph-small zocks-text-gray-600'>
              Platform used to generate the video call link for online bookings. Connect calendars in{' '}
              <NavLink to={INTEGRATION_SETTINGS_PATH}>Integration settings</NavLink>.
            </span>
          </div>
          {selectedCalendarOption ? (
            <div role='radiogroup' aria-label='Meeting platform' className='d-flex flex-wrap gap-2'>
              {PLATFORM_OPTIONS.map((option) => (
                <PlatformOptionCard
                  key={option.serviceId}
                  option={option}
                  zoomConnection={zoomConnection}
                  selectedCalendar={selectedCalendarOption}
                  selectedPlatform={userSettings.integrations?.selectedMeetingPlatform}
                  onSelect={selectPlatform}
                />
              ))}
            </div>
          ) : (
            <div className='d-flex align-items-center justify-content-center p-3 rounded-3 border zocks-border-dashed zocks-border-color-gray-300 zocks-bg-color-gray-100'>
              <span className='paragraph-small zocks-text-gray-700 text-center'>Choose a scheduling calendar first to set your meeting platform.</span>
            </div>
          )}
        </SettingsRow>
      </SettingsCard>
    </SettingsSection>
  )
}
